/*
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2022 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */

import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import siteMessage from "@insite/client-framework/SiteMessage";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Modal from "@insite/mobius/Modal";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import MarketoFormWrapper from "@stoneage/App/Components/MarketoFormModal/MarketoFormWrapper";
import { CustomAppState } from "@stoneage/App/Store/Reducers";
import { constants } from "@stoneage/XDL/constants";
import { parseBoolean } from "@stoneage/XDL/Utilities";
import { marketoDefaultCss } from "@stoneage/XDL/Utilities/SharedStyles/sharedStyles";
import * as cssLinter from "css";
import parse from "html-react-parser";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

type Props = WidgetProps &
    HasProduct &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasHistory &
    HasShellContext;

const mapStateToProps = (state: CustomAppState, ownProps: HasProduct) => ({});

const mapDispatchToProps = {};
export interface ContactUsToEnrollButtonStyles {
    button?: ButtonPresentationProps;
    message?: TypographyProps;
    whiteContainer?: InjectableCss;
    viewLink?: LinkPresentationProps;
    formWrapper?: InjectableCss;
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
}

export const contactUsToEnrollButtonStyles: ContactUsToEnrollButtonStyles = {
    button: {
        typographyProps: {
            css: css`
                &&& {
                    text-transform: none;
                    font-weight: 500;
                }
            `,
        },
        css: css`
            width: 100%;
            justify-content: center;

            &&& svg {
                stroke-width: 3px;
            }
        `,
    },
    message: {
        size: "12px",
        lineHeight: "18px",
        css: css`
            margin-bottom: 12px;
            display: block;
        `,
    },
    whiteContainer: {
        css: css`
            margin-top: 12px;
            background: white;
            padding: 8px;
            &&:nth-child(2n) {
                margin-top: 20px;
            }
            @media print {
                display: none;
            }
        `,
    },
    viewLink: {
        typographyProps: {
            size: 12,
            weight: "bold",
        },
        icon: {
            iconProps: {
                src: "IconArrowRightCopper",
                size: 9,
            },
            position: "right",
        },
        color: "common.black",
        css: css`
            text-transform: capitalize;
        `,
    },
};

const styles = contactUsToEnrollButtonStyles;

const ContactUsToEnrollButton = ({
    id,
    product,
    fields: {
        title,
        formDescription,
        formId,
        width,
        height,
        buttonText,
        displayAsButton,
        instructionMessage,
        customCss,
    },
}: Props) => {
    const isTraining = parseBoolean(product.properties["isTraining"]);
    const [showModal, setShowModal] = React.useState(false);
    const clickHandler = () => {
        setShowModal(true);
    };

    if (!isTraining) {
        return null;
    }

    return (
        <StyledWrapper
            css={css`
                margin-top: 20px;
                @media print {
                    display: none;
                }
            `}
        >
            {!displayAsButton ? (
                <StyledWrapper {...styles.whiteContainer}>
                    <Typography {...styles.message}>{siteMessage("Custom_PDP_TrainingCourseMessage")}</Typography>
                    <Link onClick={clickHandler} {...styles.viewLink}>
                        {buttonText}
                    </Link>
                </StyledWrapper>
            ) : (
                <StyledWrapper
                    css={css`
                        width: 100%;
                    `}
                >
                    <Typography {...styles.message}>{siteMessage("Custom_PDP_TrainingCourseMessage")}</Typography>
                    <Button onClick={clickHandler} {...styles.button}>
                        {buttonText}
                    </Button>
                </StyledWrapper>
            )}

            <Modal
                size={width || 700}
                headline={translate(buttonText)}
                isOpen={showModal}
                handleClose={() => setShowModal(false)}
            >
                {formDescription && (
                    <Typography
                        css={css`
                            display: block;
                        `}
                    >
                        {parse(formDescription, parserOptions)}
                    </Typography>
                )}

                <MarketoFormWrapper
                    formWidth={width}
                    formHeight={height}
                    confirmationMessage={instructionMessage}
                    formId={formId}
                    customCss={customCss}
                    inModal={false}
                    useRenderFunction={true}
                    formType="contactUs"
                />
            </Modal>
        </StyledWrapper>
    );
};

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const advnacedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

const widgetModule: WidgetModule = {
    component: withProduct(connect(mapStateToProps)(withHistory(withIsInShell(ContactUsToEnrollButton)))),
    definition: {
        displayName: "Training Button",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
        fieldDefinitions: [
            {
                name: "displayAsButton",
                displayName: "Display as Button",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                tab: basicTab,
            },
            {
                name: "buttonText",
                displayName: "Button Text",
                editorTemplate: "TextField",
                defaultValue: "Contact Us To Enroll",
                fieldType: "Translatable",
                tab: basicTab,
            },
            {
                name: "formDescription",
                displayName: "Body Copy",
                editorTemplate: "RichTextField",
                defaultValue: "",
                fieldType: "Translatable",
                tab: basicTab,
            },
            {
                name: "height",
                displayName: "Form Height in Pixels",
                editorTemplate: "IntegerField",
                defaultValue: constants.marketoModalDefaultDimensions.height,
                fieldType: "General",
                tab: basicTab,
            },
            {
                name: "width",
                displayName: "Form Width in Pixels",
                editorTemplate: "IntegerField",
                defaultValue: constants.marketoModalDefaultDimensions.width,
                fieldType: "General",
                tab: basicTab,
            },
            {
                name: "formId",
                displayName: "Form ID",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
                tab: basicTab,
            },
            {
                name: "instructionMessage",
                displayName: "Success Message Copy",
                editorTemplate: "RichTextField",
                defaultValue: "",
                fieldType: "Translatable",
                tab: basicTab,
            },
            {
                name: "customCss",
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                defaultValue: marketoDefaultCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
                tab: advnacedTab,
            },
        ],
    },
};

export default widgetModule;
